import cs from 'classnames';
import { Formik } from 'formik';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, Button, Form, OverlayTrigger, ProgressBar, Spinner, Tooltip,
} from 'react-bootstrap';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useHotkeys } from 'react-hotkeys-hook';
import * as Yup from 'yup';
import { useApiService } from '../../../../contexts/ApiServiceContext/ApiServiceContext';
import { useRenpyApiService } from '../../../../contexts/RenpyApiServiceContext';
import { useAsyncOperationState } from '../../../../dorian-shared/hooks/useAsyncOperationState';
import { logger } from '../../../../services/loggerService/loggerService';
import { isRenpyUploadEnabled } from '../../../utils/userActionUtils';

function DefaultDropZoneInstruction() {
  return <span>Drag & drop a .zip file here, or click to select</span>;
}

function DefaultActiveDropZoneInstruction() {
  return <span>Drop the .zip file here...</span>;
}

export interface RenPyGameSettingsFormData {
  gameFile?: File;
  buildUrl?: string;
}

export interface RenPyGameSettingsProps {
  episode: {
    id: number;
    uuid: string;
    revision?: number;
    renpyContentUrl?: string | null;
    renpyOrientation?: number;
  };
  bookUuid: string;
  isAdmin?: boolean;
  onSaveSuccess?: () => void | Promise<void>;
  isEditable?: boolean;
}

type UploadState = 'idle' | 'uploading' | 'uploaded' | 'error';

// File size limit constants
const MAX_FILE_SIZE_MB = 1000;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const validationSchema = Yup.object().shape({
  gameFile: Yup.mixed()
    .test('fileSize', `File size must not exceed ${MAX_FILE_SIZE_MB}MB`, (value) => {
      if (!value) return true;
      return (value as File).size <= MAX_FILE_SIZE_BYTES;
    })
    .test('fileType', 'Only .zip files are allowed', (value) => {
      if (!value) return true;
      const file = value as File;
      return file.type === 'application/zip' || file.name.endsWith('.zip');
    }),
});

const formatValidationErrors = (errors: Array<{ rule?: string; message?: string }>) => errors
  .map(({ rule, message }) => {
    if (rule && message) {
      return `✗ ${message}`;
    }
    return `✗ ${message}` || rule || 'Unknown validation error';
  })
  .join('\n');

export function RenPyGameSettings({
  episode, bookUuid, isAdmin = false, onSaveSuccess, isEditable = true,
}: RenPyGameSettingsProps) {
  const apiService = useApiService();
  const renpyApiService = useRenpyApiService();

  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [contentUrl, setContentUrl] = useState<string | null>(episode.renpyContentUrl || null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editedUrl, setEditedUrl] = useState<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const setFieldValueRef = useRef<((field: string, value: unknown) => void) | null>(null);

  const [, {
    isLoading: isSaving,
    setToLoading: setSaving,
    isError: isSaveError,
    setToError: setSaveError,
    setToSuccess: setSaveSuccess,
    statusMessage: saveStatusMessage,
  }] = useAsyncOperationState();

  const canUpload = isRenpyUploadEnabled() && isEditable;

  // Sync contentUrl with episode data when it changes (after reload from editor-api)
  useEffect(() => {
    setContentUrl(episode.renpyContentUrl || null);
  }, [episode.renpyContentUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: unknown) => void) => {
    const file = event.target.files?.[0];

    if (file) {
      // Validate file type (fallback for non-dropzone inputs)
      if (!file.name.toLowerCase().endsWith('.zip')) {
        const target = event.target as HTMLInputElement;
        target.value = '';
        setFieldValue('gameFile', null);
        setUploadState('error');
        setUploadErrorMessage('Invalid file type. Only .zip files are allowed.');
        logger.info(`File "${file.name}" has invalid type. Only .zip files are allowed.`);
        return;
      }

      // Validate file size (fallback for non-dropzone inputs)
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const target = event.target as HTMLInputElement;
        target.value = '';
        setFieldValue('gameFile', null);
        setUploadState('error');
        setUploadErrorMessage(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please choose a smaller file.`);
        logger.info(`File "${file.name}" is too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum allowed: ${MAX_FILE_SIZE_MB}MB`);
        return;
      }

      setFieldValue('gameFile', file);
      // Reset states when new file is selected
      setUploadState('idle');
      setUploadProgress(0);
      setUploadErrorMessage(null);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      const firstRejection = rejectedFiles[0];
      const firstError = firstRejection.errors[0];

      let errorMessage = 'File upload failed';

      if (firstError.code === 'file-invalid-type') {
        errorMessage = 'Invalid file type. Only .zip files are allowed.';
      } else if (firstError.code === 'file-too-large') {
        errorMessage = `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please choose a smaller file.`;
      } else {
        errorMessage = firstError.message;
      }

      logger.info('File rejected:', firstRejection.errors);
      setFieldValueRef.current?.('gameFile', null);
      setUploadState('error');
      setUploadErrorMessage(errorMessage);
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (setFieldValueRef.current) {
        handleFileChange({ target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>, setFieldValueRef.current);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
    },
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: false,
    disabled: uploadState === 'uploading' || !canUpload,
  });

  const initialValues: RenPyGameSettingsFormData = {
    gameFile: undefined,
  };

  const handleCancelUpload = useCallback(() => {
    if (uploadState === 'uploading' && abortControllerRef.current) {
      abortControllerRef.current.abort();
      setUploadState('error');
      setUploadProgress(0);
      setUploadErrorMessage('Upload cancelled by user');
      abortControllerRef.current = null;
      logger.info('Upload cancelled by user');
    }
  }, [uploadState]);

  // Hotkey for canceling upload (Escape key)
  useHotkeys('escape', handleCancelUpload, {
    enableOnFormTags: true,
    preventDefault: true,
  }, [handleCancelUpload]);

  const handleSave = async (urlToSave?: string) => {
    const urlToUpdate = urlToSave || contentUrl;
    if (!urlToUpdate) {
      setSaveError('No content URL to save');
      return;
    }

    try {
      setSaving('Saving content URL...');
      await apiService.updateEpisodeRenpyContentUrl(episode.id, urlToUpdate, episode.renpyOrientation);

      // Reload episode data from editor-api to sync state
      if (onSaveSuccess) {
        await onSaveSuccess();
      }

      setSaveSuccess('Content URL saved successfully');
      logger.info('Content URL saved:', urlToUpdate);

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess('');
      }, 3000);
    } catch (error) {
      logger.error('Error saving content URL:', error);
      setSaveError('Failed to save content URL. Please try again.');
    }
  };

  const handleUpload = async (values: RenPyGameSettingsFormData) => {
    const { gameFile: gameFileValue } = values;

    if (!gameFileValue) {
      setUploadState('error');
      setUploadErrorMessage('Missing required data for upload');
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setUploadState('uploading');
      setUploadProgress(0);
      setUploadErrorMessage(null);

      const formData = new FormData();
      formData.append('gameFile', gameFileValue);

      const config = {
        signal: controller.signal,
        onUploadProgress: (progressEvent: ProgressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      };

      // Use episode's actual revision
      const revision = episode.revision?.toString() || 'latest';

      logger.info('Uploading RenPy game:', { bookUuid, episodeUuid: episode.uuid, revision });

      const response = await renpyApiService.uploadRenpyGame(
        bookUuid,
        episode.uuid,
        revision,
        formData,
        config,
      );

      if (response.success && response.indexUrl) {
        setUploadState('uploaded');
        logger.info('RenPy game uploaded successfully:', response);

        // Save the content URL to editor-api and reload episode data to sync state
        await handleSave(response.indexUrl);
      } else if (response.errors && response.errors.length > 0) {
        setUploadState('error');
        setUploadErrorMessage(formatValidationErrors(response.errors));
        logger.error('RenPy game validation failed:', response.errors);
      } else {
        setUploadState('error');
        setUploadErrorMessage(response.error || 'Upload failed for unknown reason');
        logger.error('RenPy game upload failed:', response);
      }
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        logger.info('Upload was cancelled');
        setUploadState('idle');
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Backend returned 400 with validation errors
        setUploadState('error');
        setUploadErrorMessage(formatValidationErrors(error.response.data.errors));
        logger.error('RenPy game validation failed:', error.response.data.errors);
      } else {
        logger.error('Error uploading RenPy game:', error);
        setUploadState('error');
        setUploadErrorMessage('Failed to upload game file. Please try again.');
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleCopyUrl = useCallback(async () => {
    if (!contentUrl) return;

    try {
      await navigator.clipboard.writeText(contentUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      logger.info('Content URL copied to clipboard');
    } catch (error) {
      logger.error('Failed to copy URL:', error);
    }
  }, [contentUrl]);

  const handleUnlockUrl = useCallback(() => {
    setEditedUrl(contentUrl || '');
    setIsEditingUrl(true);
    logger.info('Admin unlocked URL for manual editing');
  }, [contentUrl]);

  const handleCancelEditUrl = useCallback(() => {
    setEditedUrl('');
    setIsEditingUrl(false);
    logger.info('Admin cancelled URL editing');
  }, []);

  const handleSaveEditedUrl = useCallback(async () => {
    if (!editedUrl.trim()) {
      setSaveError('URL cannot be empty');
      return;
    }

    try {
      setSaving('Saving manually edited URL...');
      await apiService.updateEpisodeRenpyContentUrl(episode.id, editedUrl.trim(), episode.renpyOrientation);

      // Reload episode data from editor-api to sync state
      if (onSaveSuccess) {
        await onSaveSuccess();
      }

      setIsEditingUrl(false);
      setSaveSuccess('Manually edited URL saved successfully');
      logger.info('Admin saved manually edited URL:', editedUrl);

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess('');
      }, 3000);
    } catch (error) {
      logger.error('Error saving manually edited URL:', error);
      setSaveError('Failed to save URL. Please try again.');
    }
  }, [editedUrl, apiService, episode.id, episode.renpyOrientation, onSaveSuccess, setSaving, setSaveSuccess, setSaveError]);
  // Defensive check for required props
  // TODO: We need move to parrent component level error handling
  if (!episode || !bookUuid) {
    logger.error('RenPyGameSettings: Missing required props', { episode, bookUuid });
    return (
      <Alert variant="danger">
        Unable to load RenPy Game Settings. Missing required episode or book information.
      </Alert>
    );
  }

  return (
    <>
      {/* Full screen overlay during loading */}
      {uploadState === 'uploading' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div className="text-center text-white">
            <Spinner
              animation="border"
              variant="primary"
              className="mb-3"
              style={{ width: '3rem', height: '3rem' }}
            />
            <h5 className="mb-3">Uploading Game File...</h5>
            <div className="mb-4" style={{ width: '300px', margin: '0 auto' }}>
              <ProgressBar
                now={uploadProgress}
                variant="primary"
                striped
                animated
                className="mb-2"
                label={`${uploadProgress}%`}
              />
              <p className="mb-3 text-white small">
                Press
                {' '}
                <kbd className="bg-primary">Escape</kbd>
                {' '}
                to cancel
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCancelUpload}
                className="mt-2"
              >
                Cancel Upload
              </Button>
            </div>
          </div>
        </div>
      )}

      <div>
        <Alert variant="warning">
          <p>
            This functionality is in the alpha stage, and there may be some inaccuracies or errors. We are working on
            improvements and appreciate your understanding!
          </p>
        </Alert>

        {!isEditable && (
          <Alert variant="danger">
            <strong>⚠️ Episode is Published</strong>
            <p className="mb-0">
              This episode is currently live and cannot be modified. Uploading and saving are disabled.
            </p>
          </Alert>
        )}

        {!isRenpyUploadEnabled() && (
          <Alert variant="info">
            <strong>Upload Access Required</strong>
            <p className="mb-0">
              Uploading Ren&apos;Py games requires upload permissions. Please contact an administrator if you need access.
            </p>
          </Alert>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleUpload(values)} // Fallback for form submission, but we use custom buttons
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleSubmit: formikHandleSubmit,
          }) => {
            // Store setFieldValue in ref for use in dropzone callback
            setFieldValueRef.current = setFieldValue;

            return (
              <Form onSubmit={formikHandleSubmit}>
                <div className="mb-3 d-flex align-items-start">
                  <Form.Group className="flex-grow-1 mr-3" controlId="gameFile">
                    <Form.Label>Game File</Form.Label>
                    <div
                      {...getRootProps()}
                      className={cs(
                        'border',
                        'rounded',
                        'p-3',
                        'text-center',
                        {
                          'border-light': isDragActive,
                          'bg-light': isDragActive,
                          'bg-opacity-10': values.gameFile,
                        },
                      )}
                      style={{
                        cursor: uploadState === 'uploading' || !canUpload ? 'not-allowed' : 'pointer',
                        opacity: uploadState === 'uploading' || !canUpload ? 0.6 : 1,
                      }}
                    >
                      <input {...getInputProps()} />
                      {values.gameFile ? (
                        <div>
                          <div className="text-primary fw-bold mb-1">✓ File selected</div>
                          <div className="small">
                            {values.gameFile.name}
                            {' '}
                            (
                            {(values.gameFile.size / 1024 / 1024).toFixed(2)}
                            {' '}
                            MB)
                          </div>
                        </div>
                      ) : (
                        <div>
                          {isDragActive ? (
                            <DefaultActiveDropZoneInstruction />
                          ) : (
                            <DefaultDropZoneInstruction />
                          )}
                          <div className="text-muted small mt-1">
                            Maximum file size:
                            {' '}
                            {MAX_FILE_SIZE_MB}
                            MB
                          </div>
                        </div>
                      )}
                    </div>
                    {touched.gameFile && errors.gameFile && (
                    <Form.Text className="text-danger">{errors.gameFile}</Form.Text>
                    )}
                  </Form.Group>
                </div>

                <Alert variant="light" className="mb-3">
                  {contentUrl ? (
                    <>
                      <h6 className="alert-heading mb-2">🎮 Game Content URL</h6>
                      <p className="mb-2">
                        Your RenPy game is available at the URL below. Click the button to copy it to your clipboard.
                      </p>
                      {isEditingUrl ? (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Edit Content URL</Form.Label>
                            <Form.Control
                              type="text"
                              value={editedUrl}
                              onChange={(e) => setEditedUrl(e.target.value)}
                              placeholder="Enter content URL"
                              autoFocus
                            />
                            <Form.Text className="text-warning">
                              ⚠️ Warning: Manually editing the URL bypasses the standard upload process. Only modify this if you know what you're doing.
                            </Form.Text>
                          </Form.Group>
                          <div className="d-flex gap-2 justify-content-end">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleSaveEditedUrl}
                              disabled={isSaving || !editedUrl.trim() || !isEditable}
                            >
                              {isSaving ? 'Saving...' : 'Save URL'}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={handleCancelEditUrl}
                              disabled={isSaving}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="border rounded p-2 bg-light flex-grow-1">
                            <code title={contentUrl} className="text-wrap small">{contentUrl}</code>
                          </div>
                          <div className="d-flex gap-2 justify-content-end">
                            <Button
                              variant={isCopied ? 'success' : 'outline-primary'}
                              size="sm"
                              onClick={handleCopyUrl}
                              style={{ minWidth: '80px' }}
                            >
                              {isCopied ? '✓ Copied!' : 'Copy URL'}
                            </Button>
                            {isAdmin && (
                            <OverlayTrigger
                              placement="top"
                              overlay={(
                                <Tooltip id="edit-url-tooltip">
                                  <strong>Admin Only:</strong>
                                  {' '}
                                  Manually override the content URL.
                                  <br />
                                  This is normally not necessary - use the upload feature instead.
                                  {!isEditable && (
                                    <>
                                      <br />
                                      <strong>Disabled:</strong>
                                      {' '}
                                      Episode is published.
                                    </>
                                  )}
                                </Tooltip>
                                )}
                            >
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={handleUnlockUrl}
                                style={{ minWidth: '80px' }}
                                disabled={!isEditable}
                              >
                                🔓 Edit
                              </Button>
                            </OverlayTrigger>
                            )}
                          </div>
                        </>
                      )}
                      <hr />
                    </>
                  ) : (
                    <>
                      <h6 className="alert-heading mb-2">🎮 Game Content URL</h6>
                      <p className="mb-2 text-muted">
                        Upload a game file to get your content URL.
                      </p>
                      <div className="border rounded p-2 bg-light text-muted">
                        <em>Content URL will appear here after upload</em>
                      </div>
                    </>
                  )}
                </Alert>

                <Alert variant="light" className="d-flex justify-content-center mb-3">
                  <Button
                    variant="outline-primary"
                    disabled={!values.gameFile || uploadState === 'uploading' || !canUpload}
                    onClick={() => handleUpload(values)}
                    type="button"
                    size="lg"
                  >
                    {uploadState === 'uploading' ? 'Uploading...' : 'Upload Game'}
                  </Button>
                </Alert>

                <div className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div
                    style={{
                      minHeight: '60px',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    {uploadState === 'uploaded' && !isSaving && !isSaveError && (
                      <Alert variant="success" className="mb-2">
                        ✓ Game uploaded successfully
                      </Alert>
                    )}
                    {isSaving && (
                      <Alert variant="info" className="mb-2">
                        {saveStatusMessage}
                      </Alert>
                    )}
                    {saveStatusMessage && !isSaving && !isSaveError && (
                      <Alert variant="success" className="mb-2">
                        ✓
                        {' '}
                        {saveStatusMessage}
                      </Alert>
                    )}
                    {uploadState === 'error' && uploadErrorMessage && (
                      <Alert variant="danger" className="mb-2" style={{ whiteSpace: 'pre-line' }}>
                        {uploadErrorMessage}
                      </Alert>
                    )}
                    {isSaveError && (
                      <Alert variant="danger" className="mb-2">
                        ✗
                        {' '}
                        {saveStatusMessage}
                      </Alert>
                    )}
                    {uploadState === 'idle' && !contentUrl && (
                      <Alert variant="info" className="mb-0">
                        Select a game file, then click &quot;Upload Game&quot;
                      </Alert>
                    )}
                    {uploadState === 'idle' && contentUrl && !isSaving && !saveStatusMessage && (
                      <Alert variant="info" className="mb-0">
                        Ready to upload a new version or save the current URL
                      </Alert>
                    )}
                  </div>
                </div>

              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
}
